import type { EditorMessages } from '../de/editor.js';

export const editor: EditorMessages = {
  common: {
    byMeasure: 'Utwórz według wymiarów',
    invalidDimensions: 'Podaj prawidłowe wymiary.',
    orthogonal: 'Tylko kąty 90°',
    paste: 'Wklej',
    discard: 'Odrzuć',
    duplicate: 'Duplikuj',
    show: 'Pokaż',
    hidden: 'ukryte',
    outsideHalls: 'poza halami',
    deleteAll: 'Usuń wszystkie',
  },
  fields: {
    stepUp: 'Zwiększ {label} o 5 cm',
    stepDown: 'Zmniejsz {label} o 5 cm',
    stepUpShort: '5 cm więcej',
    stepDownShort: '5 cm mniej',
    width: 'Szerokość',
    length: 'Długość',
    depth: 'Głębokość',
    height: 'Wysokość',
    eaveHeight: 'Wysokość okapu',
    roomHeight: 'Wysokość pomieszczenia',
    wallThickness: 'Grubość ściany',
    roofType: 'Rodzaj dachu',
    kind: 'Rodzaj',
    type: 'Typ',
    category: 'Kategoria',
    count: 'Liczba',
    form: 'Kształt',
    label: 'Oznaczenie',
    color: 'Kolor',
    area: 'Powierzchnia',
    note: 'Notatka',
    location: 'Położenie',
    level: 'Kondygnacja',
    centerX: 'Środek X',
    centerY: 'Środek Y',
    positionX: 'Pozycja X',
    positionY: 'Pozycja Y',
    startX: 'Początek X',
    startY: 'Początek Y',
    endX: 'Koniec X',
    endY: 'Koniec Y',
  },
  groups: {
    dimensions: 'Wymiary',
    position: 'Położenie i obrót',
    metrics: 'Wskaźniki',
    roof: 'Dach',
    facade: 'Elewacja',
    openings: 'Bramy, drzwi, okna',
    roofElements: 'Elementy dachowe',
  },
  nouns: {
    columns: 'Słup',
    sites: 'Działka',
    buildings: 'Hala',
    office: 'Budynek biurowy',
    walls: 'Ściana',
    openings: 'Otwór',
    zones: 'Strefa',
    rooms: 'Pomieszczenie',
    objects: 'Obiekt',
    measurements: 'Wymiar',
    roofElements: 'Element dachowy',
    stairs: 'Schody',
    roads: 'Droga',
    parkings: 'Parking',
    fences: 'Ogrodzenie',
    gates: 'Brama przesuwna',
    mezzanines: 'Antresola',
    canopies: 'Daszek',
    wallImages: 'Obraz ścienny',
    railings: 'Balustrada',
    element: 'Element',
  },
  counts: {
    sites: { one: '{n} działka', few: '{n} działki', many: '{n} działek', other: '{n} działki' },
    buildings: { one: '{n} hala', few: '{n} hale', many: '{n} hal', other: '{n} hali' },
    walls: { one: '{n} ściana', few: '{n} ściany', many: '{n} ścian', other: '{n} ściany' },
    openings: { one: '{n} otwór', few: '{n} otwory', many: '{n} otworów', other: '{n} otworu' },
    zones: { one: '{n} strefa', few: '{n} strefy', many: '{n} stref', other: '{n} strefy' },
    rooms: {
      one: '{n} pomieszczenie',
      few: '{n} pomieszczenia',
      many: '{n} pomieszczeń',
      other: '{n} pomieszczenia',
    },
    objects: { one: '{n} obiekt', few: '{n} obiekty', many: '{n} obiektów', other: '{n} obiektu' },
    measurements: {
      one: '{n} wymiar',
      few: '{n} wymiary',
      many: '{n} wymiarów',
      other: '{n} wymiaru',
    },
    roofElements: {
      one: '{n} element dachowy',
      few: '{n} elementy dachowe',
      many: '{n} elementów dachowych',
      other: '{n} elementu dachowego',
    },
    stairs: { one: '{n} schody', few: '{n} schody', many: '{n} schodów', other: '{n} schodów' },
    roads: { one: '{n} droga', few: '{n} drogi', many: '{n} dróg', other: '{n} drogi' },
    parkings: {
      one: '{n} parking',
      few: '{n} parkingi',
      many: '{n} parkingów',
      other: '{n} parkingu',
    },
    fences: {
      one: '{n} ogrodzenie',
      few: '{n} ogrodzenia',
      many: '{n} ogrodzeń',
      other: '{n} ogrodzenia',
    },
    gates: {
      one: '{n} brama przesuwna',
      few: '{n} bramy przesuwne',
      many: '{n} bram przesuwnych',
      other: '{n} bramy przesuwnej',
    },
    mezzanines: {
      one: '{n} antresola',
      few: '{n} antresole',
      many: '{n} antresoli',
      other: '{n} antresoli',
    },
    canopies: { one: '{n} daszek', few: '{n} daszki', many: '{n} daszków', other: '{n} daszka' },
    wallImages: {
      one: '{n} obraz ścienny',
      few: '{n} obrazy ścienne',
      many: '{n} obrazów ściennych',
      other: '{n} obrazu ściennego',
    },
    railings: {
      one: '{n} balustrada',
      few: '{n} balustrady',
      many: '{n} balustrad',
      other: '{n} balustrady',
    },
    offices: {
      one: '{n} budynek biurowy',
      few: '{n} budynki biurowe',
      many: '{n} budynków biurowych',
      other: '{n} budynku biurowego',
    },
    stalls: {
      one: '{n} miejsce postojowe',
      few: '{n} miejsca postojowe',
      many: '{n} miejsc postojowych',
      other: '{n} miejsca postojowego',
    },
    elements: {
      one: '{n} element',
      few: '{n} elementy',
      many: '{n} elementów',
      other: '{n} elementu',
    },
  },
  remove: {
    sites: 'Usuń działkę',
    buildings: 'Usuń halę',
    office: 'Usuń budynek biurowy',
    walls: 'Usuń ścianę',
    openings: 'Usuń otwór',
    closeVoid: 'Zamknij ścianę ponownie',
    zones: 'Usuń strefę',
    rooms: 'Usuń pomieszczenie',
    objects: 'Usuń obiekt',
    measurements: 'Usuń wymiar',
    roofElements: 'Usuń element dachowy',
    stairs: 'Usuń schody',
    roads: 'Usuń drogę',
    parkings: 'Usuń parking',
    fences: 'Usuń ogrodzenie',
    gates: 'Usuń bramę przesuwną',
    mezzanines: 'Usuń antresolę',
    canopies: 'Usuń daszek',
    wallImages: 'Usuń obraz',
    railings: 'Usuń balustradę',
    element: 'Usuń element',
    many: {
      one: 'Usuń {n} element',
      few: 'Usuń {n} elementy',
      many: 'Usuń {n} elementów',
      other: 'Usuń {n} elementu',
    },
  },
  toolPanel: {
    aria: 'Narzędzia',
    modes: 'Tryb narzędzia',
  },
  tools: {
    select: 'Wybór',
    draw: 'Rysuj',
    move: 'Przesuń',
    moveShort: 'Przesuń',
    rotate: 'Obróć',
    measure: 'Mierz',
  },
  steps: {
    site: '1 · Działka',
    hall: '2 · Hala',
    office: '3 · Budynki biurowe i schody',
    interior: '4 · Podział wnętrza',
    openings: '5 · Bramy, drzwi, okna',
    objects: '6 · Obiekty',
    outdoor: '7 · Tereny zewnętrzne',
    placed: 'Rozmieszczone obiekty',
  },
  summary: {
    noSite: 'jeszcze brak',
    noHall: 'jeszcze brak',
    parcels: {
      one: '{n} działka · {area}',
      few: '{n} działki · {area}',
      many: '{n} działek · {area}',
      other: '{n} działki · {area}',
    },
    halls: {
      one: '{n} hala · {area}',
      few: '{n} hale · {area}',
      many: '{n} hal · {area}',
      other: '{n} hali · {area}',
    },
    hiddenObjects: '{n} · ukryte: {hidden}',
  },
  site: {
    draw: '✎ Narysuj działkę na planie',
    byMeasureInfo:
      'Prostokąt, trapez lub kształt L o stałych wymiarach. Następnie przez „Zmień kształt” można przeciągać narożniki i krawędzie.',
    parcels: 'Działki ({n})',
    total: 'Razem',
    addFree: 'Kolejna działka według wymiarów (dowolne położenie)',
    addInfo:
      'Pojawia się na prawo od istniejących działek. Następnie zaznacz ją i przeciągnij do sąsiednich – przyciąga się do narożników i krawędzi.',
    replaceAll: 'Utwórz od nowa (zastępuje wszystkie działki)',
    shapes: { rect: 'Prostokąt', trapez: 'Trapez', l: 'Kształt L' },
    invalidSize: 'Podaj prawidłową szerokość i długość.',
    cutTooLarge: 'Wycięcie musi być mniejsze niż działka.',
    invalidTop: 'Podaj prawidłową szerokość górną.',
    confirmReplace: 'Zastąpić wszystkie istniejące działki?',
    formAria: 'Utwórz działkę',
    shapeAria: 'Kształt działki',
    widthBottom: 'Szerokość dolna',
    widthTop: 'Szerokość górna',
    shift: 'Przesunięcie górne (puste = środek)',
    cutWidth: 'Szerokość wycięcia',
    cutLength: 'Długość wycięcia',
    replace: 'Zastąp działkę',
    add: 'Dodaj działkę',
    create: 'Utwórz działkę',
  },
  sitePanel: {
    sides: { north: 'Północ', east: 'Wschód', south: 'Południe', west: 'Zachód' },
    attachTitle: 'Dołącz do „{name}”',
    attachInfoEast:
      'Prostokątna działka sąsiednia przylegająca do jednego boku. Przesunięcie na wschód, ujemne w przeciwnym kierunku. Potem można ją dowolnie przesuwać; przyciąga się do sąsiednich krawędzi.',
    attachInfoNorth:
      'Prostokątna działka sąsiednia przylegająca do jednego boku. Przesunięcie na północ, ujemne w przeciwnym kierunku. Potem można ją dowolnie przesuwać; przyciąga się do sąsiednich krawędzi.',
    attachAria: 'Dołącz działkę',
    side: 'Strona',
    alongLength: 'Długość wzdłuż krawędzi',
    offsetAlong: 'Przesunięcie wzdłuż krawędzi',
    attach: '＋ Dołącz działkę',
    straighten: 'Wyprostuj',
    straightenBy: 'Wyprostuj ({deg}°)',
    straightenTitle: 'Obraca tak, aby najdłuższa krawędź działki była pozioma lub pionowa',
    selectAll: 'Zaznacz wszystkie działki',
    rotateBackground: 'Obracaj też obraz tła',
    moveBackground: 'Przesuwaj też obraz tła',
    nameLabel: 'Oznaczenie (np. numer działki)',
    perimeter: 'Obwód',
    extent: 'Wymiary całkowite',
    allSites: 'Wszystkie działki ({n})',
    rectDims: 'Wymiary (prostokąt)',
    shapeInfo:
      'Przeciągaj narożniki dowolnie, np. do trapezu. Strzałki na krawędziach przesuwają całą krawędź równolegle. Kliknięcie prawym przyciskiem na krawędzi wstawia tam narożnik.',
    shapeStop: 'Zakończ zmianę kształtu',
    shapeStart: 'Zmień kształt (przeciągaj narożniki i krawędzie)',
    vertices: 'Narożniki i krawędzie jako liczby',
    positionInfo:
      'Przeciągnij działkę na planie, aby ją przesunąć – przyciąga się do sąsiednich działek. „Wyprostuj” obraca najdłuższą krawędź do poziomu lub pionu.',
  },
  interior: {
    byMeasureInfo:
      'Pojawia się na środku wybranej hali i można ją potem przesunąć. Strefy i pomieszczenia o dowolnym kształcie rysujesz narzędziem „Rysuj”.',
    formAria: 'Utwórz strefę lub pomieszczenie',
    createZone: 'Utwórz strefę',
    createRoom: 'Utwórz pomieszczenie',
  },
  draw: {
    title: 'Rysuj',
    what: 'Co rysować',
    kinds: {
      site: 'Działka',
      zone: 'Strefa',
      room: 'Pomieszczenie',
      wall: 'Ściana',
      road: 'Droga',
      parking: 'Parking',
      fence: 'Ogrodzenie',
      hall: 'Hala',
      office: 'Budynek biurowy',
      storey: 'Obrys kondygnacji',
      mezzanine: 'Antresola',
      railing: 'Balustrada',
      column: 'Słup',
    },
    hints: {
      column:
        'Kliknij miejsce w hali lub na kondygnacji biurowca, gdzie ma stać słup. Wysokość wynika automatycznie (do antresoli, stropu lub okapu).',
      storey:
        'Klikaj narożniki mniejszej wyższej kondygnacji na planie, najlepiej na narożnikach budynku. Zakończ kliknięciem pierwszego punktu, dwukrotnym kliknięciem lub klawiszem Enter.',
      office:
        'Klikaj kolejno narożniki budynku biurowego (np. na planie sytuacyjnym). Zakończ kliknięciem pierwszego punktu, dwukrotnym kliknięciem lub klawiszem Enter. Kondygnacje i wysokości pochodzą z formularza; obrys wewnątrz hali staje się biurem wbudowanym.',
      railing:
        'Klikaj kolejno punkty, np. wzdłuż rampy lub galerii. Zakończ dwukrotnym kliknięciem, klawiszem Enter lub przyciskiem. Balustrada stoi na aktualnie wybranym poziomie.',
      mezzanine:
        'Klikaj narożniki antresoli wewnątrz hali. Zakończ kliknięciem pierwszego punktu, dwukrotnym kliknięciem lub klawiszem Enter. Staje się 1. piętrem hali.',
      hall: 'Klikaj kolejno narożniki hali (np. na planie sytuacyjnym). Zakończ kliknięciem pierwszego punktu, dwukrotnym kliknięciem lub klawiszem Enter. Dla dachu płaskiego i jednospadowego; narożniki można potem przesuwać, wstawiać i usuwać.',
      wall: 'Przeciągaj na planie od punktu do punktu. Narożniki i ściany są przyciągane.',
      road: 'Klikaj kolejno punkty (np. na planie sytuacyjnym). Zakończ dwukrotnym kliknięciem, klawiszem Enter lub przyciskiem. Rozpoczęcie na końcu drogi ją przedłuża.',
      fence:
        'Klikaj kolejno punkty. Zakończ dwukrotnym kliknięciem, klawiszem Enter lub przyciskiem. Rozpoczęcie na końcu ogrodzenia je przedłuża.',
      parking:
        'Kliknij początek i koniec rzędu miejsc postojowych, a potem głębokość – także ukośnie do siatki. Albo szybko przeciągnij prostokąt.',
      zone: 'Klikaj kolejno narożniki, zakończ kliknięciem pierwszego punktu, dwukrotnym kliknięciem lub klawiszem Enter. Albo szybko przeciągnij prostokąt.',
      room: 'Przeciągnij prostokąt na planie, a następnie popraw wymiary po prawej.',
      site: 'Klikaj kolejno narożniki (np. na planie sytuacyjnym). Zakończ kliknięciem pierwszego punktu, dwukrotnym kliknięciem lub klawiszem Enter. Punkty przyciągają się do sąsiednich działek.',
    },
    wallRules: {
      title: 'Zasady ścian',
      items: [
        'Przy fasadzie: ściana kończy się na wewnętrznej stronie ściany zewnętrznej, nawet jeśli przeciągniesz ją do 50 cm dalej. Szczeliny do 15 cm są zamykane.',
        'Przy innych ścianach: wystające końce do 25 cm są cofane na równo, szczeliny do 15 cm są zamykane. Większe występy i przejścia pozostają.',
        'Przesuwanie (mysz, S, strzałki): ściany w połączeniu T lub w niedokładnym narożniku są wydłużane lub skracane i pozostają połączone (do 5 cm liczy się jako połączenie). Dokładne narożniki przesuwają się jako narożnik.',
        'Ściany zablokowane i ściany zewnętrzne budynków nie są zmieniane.',
        'Klawisze: S przesuń, E zmień rozmiar, Alt podczas przeciągania wyłącza przyciąganie.',
      ],
    },
    zoneCategory: 'Kategoria strefy',
    roomType: 'Rodzaj pomieszczenia',
    wallType: 'Rodzaj ściany',
    roadKind: 'Rodzaj drogi',
    parkingKind: 'Rodzaj miejsc postojowych',
    firstPoint: 'Ustaw pierwszy narożnik na planie',
    points: {
      one: 'Ustawiono {n} narożnik',
      few: 'Ustawiono {n} narożniki',
      many: 'Ustawiono {n} narożników',
      other: 'Ustawiono {n} narożnika',
    },
    removeLast: 'Usuń ostatni',
    finishSite: 'Zakończ działkę',
    finishZone: 'Zakończ powierzchnię',
    finishRoad: 'Zakończ drogę',
    finishFence: 'Zakończ ogrodzenie',
    finishHall: 'Zakończ halę',
    finishOffice: 'Zakończ budynek biurowy',
    finishStorey: 'Zakończ obrys kondygnacji',
    finishMezzanine: 'Zakończ antresolę',
    finishRailing: 'Zakończ balustradę',
    firstLinePoint: 'Ustaw punkt początkowy na planie',
    linePoints: {
      one: 'Ustawiono {n} punkt',
      few: 'Ustawiono {n} punkty',
      many: 'Ustawiono {n} punktów',
      other: 'Ustawiono {n} punktu',
    },
    rowSteps: [
      'Ustaw początek rzędu miejsc postojowych',
      'Ustaw koniec rzędu',
      'Ustaw głębokość (strona miejsc)',
    ],
  },
  mezzanine: {
    areas: 'Obszary',
    areasInfo:
      'Ściany na antresoli dzielą ją na obszary (bez grubości ścian i okładzin, drzwi rozdzielają). Nazwa zastępuje „Obszar n” na planie, w zestawieniu powierzchni i w PDF; pozostaw puste, aby był numer.',
    areaRow: 'Obszar {n} · {area}',
    defaultName: 'Antresola {n}',
    draw: 'Narysuj antresolę',
    drawInfo: 'Użytkowa część 1. piętra hali ze stropem, balustradą, słupami i schodami.',
    elevation: 'Poziom podłogi',
    slab: 'Grubość stropu',
    railing: 'Balustrada na wolnych krawędziach',
    columns: 'Słupy',
    color: 'Kolor',
    area: 'Powierzchnia',
    headroom: 'Wysokość w świetle pod spodem',
    above: 'Wysokość w świetle nad (do okapu)',
    addStair: 'Dodaj schody',
    stairs: { one: '{n} schody', few: '{n} schody', many: '{n} schodów', other: '{n} schodów' },
    showLevel: 'Pokaż 1. piętro',
    toHall: 'Do hali {name}',
    info: 'Przeciągaj narożniki na planie 2D; prawy klik na krawędzi wstawia narożnik. Na 1. piętrze można planować strefy, pomieszczenia i obiekty.',
  },
  wallImage: {
    section: 'Obrazy i logo',
    sectionInfo:
      'Umieść napis firmowy, logo lub obraz na tej ścianie: PNG (przezroczystość zostaje zachowana) lub JPEG. Wisi tuż przed ścianą, przesuwa się razem z nią, w 3D przesuwa się go strzałkami, a narożnikami skaluje w trybie „Zmień rozmiar” (E).',
    add: 'Dodaj obraz …',
    adding: 'Przesyłanie …',
    none: 'Na tej ścianie nie ma jeszcze obrazów.',
    failed: 'Nie udało się przesłać obrazu.',
    info: 'Położenie i rozmiar na ścianie; przezroczyste części PNG pokazują ścianę.',
    offset: 'Odległość od początku ściany',
    bottom: 'Dolna krawędź nad podłogą',
    width: 'Szerokość',
    height: 'Wysokość',
    keepAspect: 'Zachowaj proporcje',
    flip: 'Na drugą stronę ściany',
    center: 'Na środku ściany',
    toWall: 'Do ściany',
    pixels: '{width} × {height} px',
    copy: 'Kopiuj (Ctrl+C)',
    copied: 'Skopiowano – wybierz ścianę docelową i naciśnij Ctrl+V',
    paste: {
      one: 'Wklej skopiowany obraz',
      few: 'Wklej {n} skopiowane obrazy',
      many: 'Wklej {n} skopiowanych obrazów',
      other: 'Wklej {n} skopiowanego obrazu',
    },
    used: 'Użyte w projekcie',
    usedHint: 'Dotknij, aby umieścić go na tej ścianie w ostatnio ustawionym rozmiarze.',
  },
  canopy: {
    title: 'Daszek',
    overOpening: 'Daszek nad tym otworem',
    overGates: 'Daszek nad wszystkimi bramami tej ściany',
    facade: 'Elewacja',
    facadeInfo:
      'Otwórz całą ścianę, np. tam, gdzie dwie hale są do siebie dobudowane. Części elewacji otwiera typ otworu „Otwarta część ściany”; ściana powyżej pozostaje.',
    openWall: 'Otwórz całą ścianę',
    addToWall: 'Daszek na tej ścianie',
    depth: 'Wysięg',
    height: 'Wysokość spodu',
    pitch: 'Nachylenie',
    posts: 'Słupy pod przednią krawędzią',
    flip: 'Na drugą stronę ściany',
    info: 'Wisi na ścianie i przesuwa się razem z nią. Przeciągaj wzdłuż ściany na planie; uchwyty zmieniają szerokość i wysięg.',
  },
  railing: {
    update: 'Zmień balustradę',
    info: 'Podczas rysowania rozpoznawane jest, na czym stoi balustrada: antresola, piętro biurowe lub dach biura; w przeciwnym razie teren (np. przy rampach). Płaskie dachy hal i własne wysokości wybierz tutaj w „Ustawienie”.',
    mount: 'Ustawienie',
    auto: 'Rozpoznaj automatycznie',
    ground: 'Parter / teren',
    roofOf: 'Dach: {name}',
    custom: 'Własna wysokość',
    elevation: 'Wysokość powierzchni',
  },
  ral: {
    label: 'Kolor RAL',
    custom: 'Własny kolor',
    common: 'Typowe kolory firmowe',
  },
  hall: {
    facadeKind: 'Rodzaj elewacji',
    facadeKinds: {
      'iso-vertical': 'Płyty warstwowe pionowo',
      'iso-horizontal': 'Płyty warstwowe poziomo',
      trapezoid: 'Blacha trapezowa',
      smooth: 'Gładka (tynk)',
      brick: 'Klinkier',
    },
    brickColors: { red: 'Klinkier czerwony', brown: 'Klinkier brązowy', yellow: 'Klinkier żółty' },
    facadeColor: 'Kolor elewacji',
    trimColor: 'Obróbki i krawędź dachu',
    gateColor: 'Kolor bram',
    roofColor: 'Kolor dachu',
    storeyColor: 'Elewacja {level}',
    likeBuilding: 'Jak budynek',
    standardRoof: 'Standardowy',
    floorKind: 'Posadzka hali',
    floorKinds: {
      concrete: 'Beton zatarty',
      coated: 'Posadzka przemysłowa powlekana',
      plain: 'Jednolity kolor',
    },
    facadeInfo:
      'Widoczne w 3D, na zrzutach ekranu i w widokach 3D w PDF. Płyty warstwowe w rastrze 1,00 m; kolor obróbek dotyczy narożników, krawędzi dachu, rynny oraz ościeżnic bram, drzwi i okien.',
    create: 'Utwórz halę',
    invalid: 'Podaj prawidłowe wymiary hali.',
    roofTypes: { gable: 'Dach dwuspadowy', mono: 'Dach jednospadowy', flat: 'Dach płaski' },
    pitch: 'Nachylenie dachu',
    areaLine: 'Powierzchnia zabudowy {built} · powierzchnia użytkowa {net}',
    areaLineOffice: 'Powierzchnia zabudowy {built}',
    areaLineFloors:
      'Powierzchnia zabudowy {built} · powierzchnia całkowita {gross} ({n} kondygnacje)',
    pitchInfo: 'odpowiada spadkowi {percent} % · dozwolone {min}–{max}°',
    drawInPlan: 'Narysuj halę na planie',
    drawRoofOnly: 'Rysowane hale mają dach płaski lub jednospadowy.',
    eaveEdge: 'Krawędź okapu (niższa strona)',
    edgeOption: 'Krawędź {n} · {length} · {dir}',
    directions: ['E', 'NE', 'N', 'NW', 'W', 'SW', 'S', 'SE'],
    outlineInfo:
      'Narysowany obrys z {n} narożnikami. Przeciągaj narożniki na planie 2D; prawy klik na krawędzi wstawia narożnik, prawy klik na narożniku go usuwa.',
    grossArea: 'Powierzchnia hali (zewn.)',
    netArea: 'Powierzchnia użytkowa',
    ridgeHeight: 'Wysokość kalenicy',
    openingsInfo: 'Wybierz ścianę, aby wstawić otwory pojedynczo lub w rzędzie.',
    roofElementsInfo:
      'Świetliki kopułkowe, pasma świetlne i klapy dymowe, pojedynczo lub w siatce. Dotknij rozmieszczonych elementów na rzucie, aby je przesunąć lub zmienić.',
  },
  structure: {
    title: 'Konstrukcja nośna',
    info: 'Ramy stalowe ze skosami ze słupów i rygli (dwuteowniki), rozmieszczone równomiernie w rozstawie; słupy stoją wewnątrz przy elewacji, przy płytach pionowych za ryglami ściennymi. Przy szerokości hali powyżej 30 m dochodzi rząd słupów pośrednich, domyślnie pod co drugim ryglem; rygle pomiędzy opierają się na podciągu. Rozstaw powyżej 5 m automatycznie wzmacnia ramy, od 7,5 m płatwie i rygle ścienne są z kształtowników walcowanych zamiast giętych. Profile to wstępne zwymiarowanie orientacyjne (obciążenie śniegiem z ustawień projektu w „Obciążenia”) i nie zastępują obliczeń statycznych.',
    enabled: 'Wyznacz i pokaż konstrukcję',
    system: 'Sposób budowy',
    systems: {
      steel: 'Ramy stalowe',
      hybrid: 'Słupy żelbetowe z dźwigarami stalowymi',
      precast: 'Prefabrykaty (słupy i dźwigary sprężone)',
    },
    systemsInfo:
      'Słupy żelbetowe są utwierdzone w stopach kielichowych (przekrój ok. wysokość/18, co najmniej 40 cm) i stężają ściany. Rozstaw dźwigarów domyślnie 7,50 m; słupy pośrednie dopiero od 30 m (dźwigary stalowe) lub 40 m rozpiętości (sprężone). Dźwigary stalowe opierają się przegubowo; sprężone górą idą za spadkiem dachu, mają najwyżej ok. rozpiętość/15 wysokości, a przy bardziej stromym dachu ich spód również się wznosi.',
    concreteColumn: 'Przekrój słupa (beton)',
    girderProfile: 'Profil dźwigara (jednoprzęsłowy)',
    girder: 'Wysokość dźwigara (podpora / największa)',
    girderValue: '{support} / {deepest}',
    roofBracing: 'Stężenie dachu i rozpórki',
    bay: 'Rozstaw ram (maks.)',
    interiorRows: 'Słupy pośrednie',
    rows: ['Brak', '1 rząd', '2 rzędy', '3 rzędy'],
    interiorSpacing: 'Słupy pośrednie w',
    spacings: ['Każdej osi', 'Co 2. osi', 'Co 3. osi'],
    beamProfile: 'Podciąg (rząd środkowy)',
    auto: '{value} (auto)',
    columnProfile: 'Profil słupa',
    rafterProfile: 'Profil rygla',
    gableColumns: 'Słupy ściany szczytowej',
    purlins: 'Płatwie (dach)',
    girts: 'Rygle ścienne (dla płyt pionowych)',
    girtsOptional: 'Rygle ścienne (niepotrzebne przy tej elewacji)',
    bracing: 'Stężenia i rozpory okapowe',
    haunches: 'Wzmocnienia naroży ram (skosy)',
    conflict: '{opening} trafia na słup w osi {axis}',
    conflictGable: '{opening} trafia na słup ściany szczytowej',
    grid: 'Siatka',
    gridValue: '{n} pól × {length}',
    rafterSpan: 'Rozpiętość rygla',
    roofLoad: 'Obciążenie dachu (śnieg + ciężar własny)',
    roofLoadValue: '{value} kN/m²',
    windLoad: 'Ciśnienie wiatru qₚ (wysokość kalenicy)',
    clearWidth: 'Szerokość w świetle słupów',
    clearLength: 'Długość w świetle',
    outline: 'Dla hal o narysowanym obrysie konstrukcja nie jest jeszcze pokazywana.',
  },
  pv: {
    label: 'Instalacja PV na dachu (+{load} kN/m²)',
    share: 'Zajęta powierzchnia dachu',
    info: 'Moduły z konstrukcją jako dodatkowe obciążenie stałe dachu (ok. 20 kg/m²) dla dźwigarów, płatwi, słupów i fundamentów; w kalkulacji jako kWp z zajętej powierzchni dachu (ok. 0,2 kWp na m²).',
  },
  officeStructure: {
    title: 'Konstrukcja nośna',
    info: 'Ściany nośne: stropy opierają się na ścianach zewnętrznych i masywnych ścianach wewnętrznych; sprawdzane jest, czy strop pokona rozpiętość. Szkielet żelbetowy: słupy w siatce (elewacja dzielona równomiernie), wymiarowane z powierzchni, którą przenosi każdy słup, i wszystkich kondygnacji powyżej (biuro 3 kN/m², użytkowy strop górny 5 kN/m², dach ze śniegiem); stropy płaskie lub na podciągach. Zamknięte klatki schodowe liczą się jako trzon żelbetowy. Wstępne wymiarowanie orientacyjne, nie zastępuje obliczeń statycznych.',
    system: 'System konstrukcyjny',
    systems: {
      walls: 'Ściany nośne',
      flat: 'Szkielet żelbetowy ze stropami płaskimi',
      beams: 'Szkielet żelbetowy z podciągami',
    },
    grid: 'Siatka słupów (maks.)',
    columnSize: 'Przekrój słupa',
    auto: '{value} (auto)',
    fields: 'Siatka',
    fieldsValue: '{nx} × {ny} pól, {x} × {y}',
    columnsOf: 'Słupy {level}',
    columnsValue: '{n} szt., {size} × {size}',
    beam: 'Podciągi (b × h)',
    beamValue: '{width} × {height}',
    span: 'Rozpiętość stropu',
    slab: 'Grubość stropu',
    slabValue: '{current} (zalecane {recommended})',
    adopt: 'Przyjmij zalecaną grubość stropu {value}',
  },
  costs: {
    title: 'Kalkulacja',
    intro:
      'Ilości z planu razy ceny jednostkowe tego projektu. Ceny i narzuty można zmieniać bezpośrednio w tabeli.',
    rangeLabel: 'Zakres kosztów:',
    steelExtra: 'Narzut na połączenia (stal)',
    range: 'Zakres ±',
    morePrices: 'Pozostałe ceny jednostkowe',
    resetPrices: 'Usuń wszystkie własne ceny (użyj cen z katalogu)',
    catalog: 'Katalog cen',
    catalogDefault: 'Domyślny ({name})',
    ownPriceReset: 'Własna cena – przywróć cenę z katalogu',
    foundationsFromColumns: 'Wyznacz fundamenty ze słupów i ścian',
    foundationsFromColumnsHint:
      'Stopy fundamentowe z obciążenia każdego słupa (nacisk na grunt 250 kN/m², przy elewacji 80 cm głębokości poniżej przemarzania), ławy i fartuchy przeciwmrozowe pod ścianami; zastępuje procentowy narzut na fundamenty.',
    foundationsMeasured: 'wyznaczone',
  },
  hallSections: {
    title: 'Obszary wydzielone ścianami działowymi',
    info: 'Wyznaczane automatycznie ze ścian działowych, mierzone do osi ściany. Szczeliny do 1 m do najbliższej ściany uznaje się za zamknięte (linia przerywana na planie).',
    adoptedAs: 'Przejęto jako',
    hasHoles: 'Zawiera biuro lub pomieszczenie – narysuj strefę ręcznie.',
    zoneKindFor: 'Rodzaj strefy dla {name}',
    adopt: 'Przejmij jako strefę',
  },
  roofElements: {
    daylight: 'Udział światła dziennego w dachu',
    smokeVentArea: 'Powierzchnia klap dymowych (geometryczna)',
    typeAria: 'Element dachowy',
    arrangement: 'Układ',
    grid: 'Siatka',
    single: 'Pojedynczo',
    gableAria: 'Położenie na dachu dwuspadowym',
    halves: 'Na każdej połaci',
    ridge: 'Na kalenicy',
    roof: 'Cała powierzchnia',
    widthAcross: 'Szerokość (w poprzek)',
    lengthAlong: 'Długość (wzdłuż)',
    spacingU: 'Rozstaw osi w poprzek',
    spacingV: 'Rozstaw osi wzdłuż',
    marginHalves: 'Odstęp od okapu/kalenicy/szczytu',
    margin: 'Odstęp od krawędzi',
    ridgeHint: '„w poprzek” = w poprzek kalenicy, „wzdłuż” = wzdłuż kalenicy.',
    halvesHint: 'Symetrycznie do kalenicy, na środku obu połaci.',
    eaveHint: '„w poprzek” = w poprzek okapu, „wzdłuż” = wzdłuż okapu.',
    outlineHint:
      'Umieszczane są tylko elementy leżące w całości na dachu, łącznie z odstępem od krawędzi.',
    placeSingle: 'Umieść {type} na środku hali',
    placeSingleRoof: 'Umieść {type} na dachu',
    placeGrid: 'Umieść {n} × {type}',
    noFit: 'Przy tych odstępach nie mieści się na dachu',
    removeAll: 'Usuń wszystkie elementy dachowe tej hali',
  },
  office: {
    title: 'Budynek biurowy',
    infoIndoor:
      'W hali: stoi pod dachem hali, na górze strop betonowy, użytkowy jako powierzchnia (np. antresola magazynowa). Kondygnacje i wysokości można potem dopasować dla każdej kondygnacji.',
    infoOutdoor:
      'Wolnostojący z dachem płaskim, np. przed halą. Kondygnacje i wysokości można potem dopasować dla każdej kondygnacji.',
    formAria: 'Utwórz budynek biurowy',
    placementAria: 'Położenie budynku biurowego',
    indoor: 'W hali',
    outdoor: 'Wolnostojący',
    floors: 'Kondygnacje',
    clearHeight: 'Wysokość w świetle kondygnacji',
    slab: 'Strop z posadzką',
    invalid: 'Podaj prawidłowe wymiary (min. 2 m, wysokość w świetle min. 2 m).',
    create: 'Utwórz budynek biurowy',
    drawOutline: 'Narysuj obrys (np. trapez)',
  },
  officePanel: {
    areasInfo:
      'Narysowane ściany dzielą pokazaną kondygnację na obszary (bez grubości ścian i okładzin, drzwi rozdzielają, założone pomieszczenia liczą się osobno). Nazwa zastępuje „Obszar n” na planie i w zestawieniu powierzchni; ⌖ przybliża.',
    enterMode: 'Edytuj budynek biurowy (tryb biurowy)',
    placementIndoor: 'W hali (zabudowa)',
    placementOutdoor: 'Na zewnątrz (wolnostojący, dach płaski)',
    dimsFloors: 'Wymiary i kondygnacje',
    floorCount: 'Liczba kondygnacji',
    clearHeightOf: 'Wysokość w świetle {level}',
    usableTop: 'Strop jako antresola (balustrada, dostęp schodami)',
    deckRailing: 'Balustrada antresoli',
    sideFront: 'Przód',
    sideRight: 'Prawo',
    sideBack: 'Tył',
    sideLeft: 'Lewo',
    sideLength: '{side} ({length})',
    railingAll: 'Wszystkie',
    railingLong: 'Tylko dłuższe boki',
    railingNone: 'Brak',
    railingAutoHint:
      'Wzdłuż ściany hali lub przylegającej antresoli balustrada jest pomijana automatycznie.',
    deckStair: 'Schody na antresolę',
    storeys: 'Kondygnacje (kondygnacja wycofana)',
    storeysInfo:
      'Wyższa kondygnacja może być mniejsza niż ta poniżej: obrysuj ją na planie na tej kondygnacji. Ściany zewnętrzne podążają za nowym obrysem, wolna powierzchnia obok staje się dachem płaskim z attyką lub tarasem z balustradą. Wyższe kondygnacje przejmują obrys.',
    storeyInherited: '{level}: jak poniżej',
    storeyOwn: '{level}: własny obrys · {area}',
    storeyDraw: 'Narysuj obrys',
    storeyReset: 'Jak poniżej',
    storeyFree: 'Wolna powierzchnia obok',
    storeyFreeRoof: 'Dach płaski z attyką',
    storeyFreeTerrace: 'Taras na dachu z balustradą',
    removedPieces: 'Usunięte ściany zewnętrzne',
    removedPiecesInfo:
      'Kliknięcie ściany zewnętrznej w 3D lub na planie i usunięcie jej usuwa ją tylko na tej kondygnacji. Na wyższych kondygnacjach może tam stanąć balustrada.',
    removedPiece: '{level} · ściana {n}',
    restorePiece: 'Przywróć',
    showDeck: 'Pokaż i edytuj antresolę',
    restoreWalls: 'Przywróć ściany zewnętrzne',
    extendRooms: 'Dopasuj pomieszczenia do krawędzi stropu',
    removedWalls:
      'Usunięte ściany zewnętrzne: {n}. Pozostają usunięte przy przesuwaniu i zmianie rozmiaru.',
    totalHeight: 'Wysokość całkowita',
    floorOf: 'Posadzka {level}',
    airTo: 'Prześwit do okapu {name}',
    footprint: 'Powierzchnia zabudowy',
    gfa: 'Powierzchnia całkowita wszystkich kondygnacji',
    stairs: 'Schody',
    noStairs: 'Brak schodów – wstaw je po lewej w „3 · Budynki biurowe i schody”.',
    walls: 'Ściany zewnętrzne · drzwi, okna',
    upperWindows: 'Okna na piętrze: wybierz otwór i ustaw kondygnację w polu „Kondygnacja”.',
    unassigned: 'Nieprzypisane · {area} ({w} × {l} m)',
    restRoomType: 'Rodzaj pomieszczenia dla pozostałej powierzchni',
    adoptRoom: 'Przejmij jako pomieszczenie',
    roomBook: 'Pomieszczenia · zestawienie',
    noRooms:
      'Brak pomieszczeń. Przeciągnij pomieszczenia na planie za pomocą „Rysuj → Pomieszczenie” lub użyj szablonu poniżej.',
    unassignedOf: 'w tym nieprzypisane {area}',
    templateFor: 'Podział pomieszczeń z szablonu dla {level}',
    template: 'Szablon',
    templateAria: 'Szablon pomieszczeń',
    templateWallAria: 'Rodzaj ściany szablonu',
    templateHint: 'Kondygnację wybiera się w prawym górnym rogu planu.',
    applyTemplate: 'Zastosuj szablon do {level}',
  },
  stair: {
    title: 'Schody',
    info: 'Wewnątrz schody znajdują się w północno-wschodnim narożniku budynku biurowego, na zewnątrz – przy ścianie wschodniej. Potem można je dowolnie przesuwać i obracać.',
    insert: 'Wstaw schody',
    building: 'Budynek',
    buildingAria: 'Budynek schodów',
    needsFloor: 'Schody wymagają drugiej kondygnacji lub użytkowego stropu.',
    formAria: 'Kształt schodów',
    upTo: 'Do kondygnacji',
    from: 'Od kondygnacji',
    stairwellHint:
      'Po jednych schodach na kondygnację, jedne nad drugimi; przesuwanie, zmiany i usuwanie dotyczą wszystkich.',
    fromAria: 'Schody z kondygnacji',
    width: 'Szerokość biegu',
    outside: 'Na zewnątrz budynku (dobudowane)',
    podest: 'Głębokość spocznika wyjściowego',
    landingOn: 'Spocznik wyjściowy na górze',
    construction: 'Wykonanie',
    constructionSolid: 'Masywne (zamknięta bryła)',
    constructionSteel: 'Schody stalowe (otwarte, wolne pod spodem)',
    constructionSlab: 'Schody betonowe (płyta biegowa, pod spodem wolne)',
    goingOwn: 'Szerokość stopnia (cm)',
    riserCount: 'Liczba stopni',
    standard: 'standard',
    entry: 'Spocznik przed pierwszym stopniem',
    restoreStairwell: 'Przywróć ściany klatki schodowej',
    protrudes:
      'Klatka schodowa wystaje poza budynek. Zmniejsz szerokość stopnia, liczbę stopni lub spocznik albo przesuń schody.',
    withDoor: 'Wstaw drzwi ze spocznika na piętro',
    enclosed: 'Klatka schodowa ze ścianami',
    midLanding: 'Spocznik pośredni (bieg podzielony w połowie, 1 m)',
    sideExit: 'Wyjście z boku',
    sideExitNone: 'Brak (na wprost)',
    sideExitLeft: 'W lewo',
    sideExitRight: 'W prawo',
    sideExitLength: 'Długość podestu',
    sideExitToWall: 'Wydłuż do ściany',
    sideExitNoWall: 'W tym kierunku w promieniu 20 m nie ma ściany na wysokości podestu.',
    sideExitHint:
      'Podest na górze o szerokości schodów, np. ponad słupami i ryglami ściany hali do drzwi biura.',
    snapping: 'Przyciąganie i dokowanie',
    snappingHint:
      'Wyłączone: schody przesuwają się swobodnie, bez siatki i bez przeskakiwania do ścian lub słupów. Na komputerze działa to też z wciśniętym klawiszem Alt.',
    startOn: 'Początek na',
    leadsTo: 'Prowadzi na',
    roofOf: 'Dach: {name}',
    hallFloor: 'Posadzka hali (parter)',
    startHeight: 'Wysokość początku',
    ownHeight: 'Własna wysokość',
    wallSides: 'Ściany klatki schodowej',
    sides: { front: 'Wejście (z drzwiami)', back: 'Tył (spocznik)', left: 'Lewa', right: 'Prawa' },
    risers: 'Stopnie',
    going: 'Szerokość stopnia',
    footprint: 'Powierzchnia zabudowy',
    turn: 'Zwrot',
    turnRight: 'w prawo',
    turnLeft: 'w lewo',
    addDoor: 'Wstaw drzwi przy spoczniku',
    floorHeight: 'Wysokość kondygnacji',
    ownRise: 'Wysokość schodów (własna)',
    autoRise: 'Wysokość znów automatyczna',
    stepRule: 'Reguła kroku (2 h + s)',
    stairFootprint: 'Powierzchnia schodów',
    asrOk:
      'Odpowiada wartościom zalecanym niemieckiej reguły ASR A1.8 (wskazówka projektowa, nie sprawdzenie według prawa budowlanego).',
    toBuilding: 'Do budynku ({name})',
  },
  fence: {
    kind: 'Rodzaj ogrodzenia',
    color: 'Kolor',
    colors: {
      ral6005: 'Zielony mech (RAL 6005)',
      ral7016: 'Antracyt (RAL 7016)',
      galvanized: 'Ocynkowany',
    },
    customColor: 'Własny kolor',
    addGate: 'Wstaw bramę przesuwną',
    addGateHere: 'Wstaw bramę przesuwną w to ogrodzenie',
    gateInfo:
      'Elektryczna, samonośna brama przesuwna. Można ją swobodnie przesuwać; w pobliżu ogrodzenia przyciąga się do jego linii i tworzy w nim przerwę.',
    gateWidth: 'Szerokość bramy',
    opensTo: 'Otwiera się w',
    left: 'lewo',
    right: 'prawo',
    addDoor: 'Wstaw furtkę',
    addDoorHere: 'Wstaw furtkę w to ogrodzenie',
    doorTitle: 'Furtka',
    removeDoor: 'Usuń furtkę',
    doorInfo:
      'Jednoskrzydłowa furtka dla pieszych. Można ją swobodnie przesuwać; w pobliżu ogrodzenia przyciąga się do jego linii i tworzy w nim przerwę. Obrót o 180° zmienia kierunek otwierania.',
    doorWidth: 'Szerokość przejścia',
    hinge: 'Zawiasy po stronie',
    gateKind: 'Rodzaj',
    kinds: { sliding: 'Brama przesuwna', door: 'Furtka' },
  },
  column: {
    title: 'Słup',
    place: 'Wstaw słup (stalowy lub żelbetowy)',
    material: 'Wykonanie',
    materials: { steel: 'Słup stalowy (HEB)', concrete: 'Słup żelbetowy' },
    height: 'Wysokość',
    auto: 'automatycznie',
    autoHeight: 'Wysokość automatyczna ({height})',
    standsOn: 'Stoi w',
    hint: 'Bez własnej wysokości słup sięga w hali do antresoli powyżej lub do okapu, a w biurowcu do stropu kondygnacji. Własna wysokość liczy się od podłogi, na której stoi.',
    remove: 'Usuń słup',
    toBuilding: 'Do „{name}”',
    mezzanineColumns: 'Słupy',
    detach: 'Edytuj słupy pojedynczo',
    detachHint:
      'Zamienia automatyczne podpory na pojedyncze słupy stalowe, które można usuwać, przesuwać i zmieniać.',
    restore: 'Przywróć automatyczne słupy',
    detached: '{n} pojedynczych słupów',
  },
  lining: {
    title: 'Okładzina z płyt g-k',
    info: 'Okładzina z płyt gipsowo-kartonowych przed ścianą, np. przed gołą ścianą hali w części biurowej na antresoli: od podłogi wybranej kondygnacji do sufitu, z parapetami przy oknach; drzwi i bramy pozostają wolne. Zmniejsza powierzchnię pomieszczeń.',
    add: 'Dodaj okładzinę',
    sideRight: 'Prawa strona ściany',
    sideLeft: 'Lewa strona ściany',
    level: 'Kondygnacja',
    from: 'Od (od początku ściany)',
    to: 'Do (od początku ściany)',
    thickness: 'Grubość',
    span: 'Wysokość',
    spanValue: '+{base} do +{top}',
    color: 'Kolor',
    remove: 'Usuń okładzinę',
  },
  handover: {
    title: 'Bramki przeładunkowe',
    info: 'Przesuwna bramka siatkowa w balustradzie, np. do podawania palet wózkiem widłowym na antresolę. Balustrada kończy się po obu stronach bramki; podczas przechodzenia bramka odsuwa się, ale nadal zabezpiecza krawędź.',
    add: 'Wstaw bramkę przeładunkową',
    edge: 'Krawędź',
    edgeOption: 'Krawędź {n} ({length})',
    offset: 'Odległość od początku krawędzi',
    width: 'Szerokość',
    remove: 'Usuń bramkę przeładunkową',
  },
  wall: {
    interior: 'Ściana wewnętrzna',
    exterior: 'Ściana zewnętrzna',
    throughMezzanine: 'Do posadzki hali',
    throughMezzanineHint:
      'Wyłączone: ściana stoi na antresoli. Włączone: sięga aż do posadzki hali; górna krawędź pozostaje.',
    cladding: 'Powierzchnia',
    claddings: {
      plain: 'Gładka / tynkowana',
      sandwich: 'Płyta warstwowa pionowo',
      'sandwich-horizontal': 'Płyta warstwowa poziomo',
      'sand-lime': 'Silikat',
      concrete: 'Beton architektoniczny',
      brick: 'Klinkier',
    },
    wallType: 'Rodzaj ściany',
    ownWallType: 'Własny (zmieniona grubość/powierzchnia)',
    direction: 'Kierunek',
    ownerHall: 'Ta ściana jest sterowana wymiarami hali „{name}”.',
    removeExterior: 'Usuń ścianę zewnętrzną',
    floorRailing: 'Balustrada',
    removeOnFloor: 'Usuń tylko na {level}',
    ownerRoom: 'Ta ściana jest sterowana wymiarami pomieszczenia „{name}”.',
    toHall: 'Do hali „{name}”',
    toRoom: 'Do pomieszczenia „{name}”',
    ownerStair: 'Ta ściana należy do klatki schodowej „{name}” i przesuwa się razem ze schodami.',
    toStair: 'Do schodów „{name}”',
    removeStairWall: 'Usuń ścianę klatki schodowej',
    openingsInfo:
      'Otwór trafia na środek ściany lub obok istniejących. Kilka otworów wstawia się w rzędzie z podanym odstępem w świetle.',
    noOpenings: 'Brak otworów w tej ścianie.',
    openingType: 'Typ otworu',
    gap: 'Odstęp',
    gapAria: 'Odstęp między otworami',
    insert: 'Wstaw',
    sill: 'Parapet',
    spread: 'Rozmieszczenie',
    spreadGap: 'Stały odstęp, wyśrodkowane',
    spreadEven: 'Równomiernie na ścianie',
    storeys: 'Na kondygnacjach',
    storeysAria: 'Kondygnacje, na których wstawiane są otwory',
    tooMany: '{n} × {width} nie mieści się na ścianie ({length}).',
    noStorey: 'Wybierz co najmniej jedną kondygnację.',
    floor: 'Kondygnacja',
    floorGround: 'Posadzka (parter)',
    floorTooHigh: 'Na tej kondygnacji otwór nie mieści się już w wysokości ściany.',
    insertAtClick: 'Pozycja: {offset} od początku ściany (miejsce kliknięcia)',
    insertCentered: 'Pozycja: na środku – kliknij ścianę, aby wybrać miejsce',
  },
  opening: {
    levelAria: 'Kondygnacja otworu',
    dimsPosition: 'Wymiary i położenie',
    dimsInfo:
      'Na rzucie lub w 3D otwór można przeciągać wzdłuż ściany, także obok innych otworów; upuszczony na inny przeskakuje do najbliższego wolnego miejsca.',
    sill: 'Wysokość parapetu (nad podłogą)',
    head: 'Górna krawędź (nad podłogą)',
    heights: 'Parapet {sill} · górna krawędź {head}',
    heightsOver: 'Parapet {sill} · górna krawędź {head} (nad {floor})',
    overFloor: 'Mierzone od podłogi: {floor}.',
    swing: 'Kierunek otwierania',
    swingInfo:
      'Na którą stronę otwiera się skrzydło; łuk w rzucie i otwieranie podczas spaceru wynikają z tego ustawienia.',
    flipSide: 'Na drugą stronę ściany',
    flipHinge: 'Zmień stronę zawiasów',
    slideTo: 'Kierunek przesuwu',
    slideInfo:
      'Skrzydło wisi na szynie jezdnej nad otworem i podczas przechodzenia przesuwa się wzdłuż ściany. Lewo i prawo widziane z zewnątrz.',
    slideLeft: '← w lewo',
    slideRight: 'w prawo →',
    otherFace: 'Druga strona ściany',
    mountSide: 'Strona montażu',
    mountInfo:
      'Osłona z wałem nawojowym i prowadnice boczne są po jednej stronie ściany; skrzydło to szara plandeka z pasem wizyjnym (1,40–2,00 m), która podczas przechodzenia szybko zwija się do góry. Wewnątrz to strona hali, w innym przypadku lewa strona ściany patrząc od jej początku.',
    mountInside: 'Wewnątrz',
    mountOutside: 'Na zewnątrz',
    slideNoRoom:
      'Po tej stronie jest za mało ściany na otwarte skrzydło (potrzeba: {length}). Zmień kierunek przesuwu lub przesuń bramę.',
    colors: 'Kolory',
    colorsInfo:
      'Własne kolory tego otworu; bez nich obowiązuje kolor obróbek i bram budynku. Przy kilku wybranych otworach kolor dotyczy wszystkich.',
    frameColor: 'Kolor ramy',
    leafColor: 'Kolor skrzydła (drzwi lub bramy)',
    likeBuilding: 'Jak budynek',
    voidPosts: 'Słupy na krawędziach (co najwyżej co 6 m)',
    offsetStart: 'Odległość od początku ściany',
    offsetEnd: 'Odległość do końca ściany',
    toWall: 'Do ściany ({length})',
    leveler: 'Pomost przeładunkowy (w hali)',
    shared:
      'Ta brama dzieli obniżony dojazd z sąsiednimi bramami; ustawia się go przy pierwszej bramie rzędu.',
    toLead: 'Do wspólnego dojazdu',
    gates: 'Rampy w tym dojeździe',
    addGate: 'Dodaj rampę',
    removeGate: 'Usuń rampę',
    railingLeft: 'Balustrada z lewej',
    railingRight: 'Balustrada z prawej',
    railingInfo:
      'Lewo i prawo patrząc z placu na bramy. Obok ściany hali balustrada może zostać pominięta.',
    approach: 'Dojazd do rampy (obniżony)',
    approachInfo:
      'Zalecenie: 16 m płaskiego stanowiska, aby naczepa stała poziomo, następnie rampa o spadku maks. 10 %. Spadek określa długość rampy.',
    pitDepth: 'Zagłębienie',
    bayWidth: 'Szerokość dojazdu',
    bayLength: 'Stanowisko (długość)',
    rampLength: 'Rampa (długość)',
    slope: 'Spadek rampy (%)',
    slopeAria: 'Spadek rampy',
    approachOffset: 'Przesunięcie dojazdu (wzdłuż ściany)',
    incline: 'Nachylenie',
    vertical: '– (uskok pionowy)',
    totalLength: 'Długość całkowita na zewnątrz',
  },
  sweep: {
    title: 'Krzywa ciągu',
    info: 'Pojazd miarodajny wg RASt 06 / EAR. Kolor: powierzchnia zajmowana przy dokładnej jeździe po linii prowadzącej; przerywana: 0,5 m odstępu bezpieczeństwa (niedokładność jazdy, lusterka); linie: ślad zewnętrznego przedniego i wewnętrznego tylnego koła. Ciemne: kabina kierowcy, strzałki: kierunek jazdy. „Dodaj jazdę do tyłu” tworzy manewr, np. do przodu obok rampy, a potem tyłem do niej. Czerwone: powierzchnia jazdy poza działką lub w budynku, pomarańczowe: tylko odstęp bezpieczeństwa. Krzywa niczego nie blokuje i można ją przesuwać, obracać, blokować i ukrywać jak obiekt; punkt startu pozostaje przy zmianach.',
    angleLess: '{step}° mniej',
    angleMore: '{step}° więcej',
    move: 'Jazda {n}',
    removeMove: 'Usuń',
    addReverse: '+ Dodaj jazdę do tyłu',
    addForward: '+ Dodaj jazdę do przodu',
    addMoveHint: 'Kolejna jazda od miejsca, w którym pojazd się zatrzymał (manewrowanie).',
    conflictSite: 'Powierzchnia jazdy wychodzi poza działkę.',
    conflictSiteMargin: 'Odstęp bezpieczeństwa sięga poza granicę działki.',
    conflictBuilding: 'Powierzchnia jazdy wchodzi w {name}.',
    conflictBuildingMargin: 'Odstęp bezpieczeństwa sięga do {name}.',
    direction: 'Kierunek jazdy',
    forward: 'Do przodu',
    reverse: 'Do tyłu (do rampy)',
    side: 'Skręt',
    left: 'W lewo',
    right: 'W prawo',
    angle: 'Kąt skrętu',
    uTurn: '180° (zawracanie)',
    angleFree: 'Dowolny kąt (°)',
    radius: 'Promień (od {min})',
    lead: 'Prosta przed',
    tail: 'Prosta po',
    leadReverse: 'Prosta wstecz przed łukiem',
    tailReverse: 'Prosta wstecz do rampy',
    vehicle: 'Pojazd',
    vehicleSize: '{length} × {width}',
    space: 'Zapotrzebowanie miejsca (z odstępem)',
  },
  object: {
    model: 'Model 3D',
    hidden: 'Ten obiekt jest ukryty.',
    levelAria: 'Kondygnacja obiektu',
    heightAboveFloor: 'Wysokość nad posadzką kondygnacji',
    heightAboveGround: 'Wysokość nad terenem',
    snapping: 'Przyciąganie i dokowanie',
    snappingHint:
      'Wyłączone: obiekt przesuwa się swobodnie, bez siatki i bez przeskakiwania do ścian lub obiektów. Na komputerze działa to dla każdego obiektu przy wciśniętym klawiszu Alt.',
    size: 'Wymiary',
    proportional: 'Skaluj proporcjonalnie',
    rackPlaces: 'Miejsca paletowe',
    rackLayout: {
      one: '{n} pole × {levels} poziomy × {perBay} palety',
      few: '{n} pola × {levels} poziomy × {perBay} palety',
      many: '{n} pól × {levels} poziomy × {perBay} palety',
      other: '{n} pola × {levels} poziomy × {perBay} palety',
    },
    rackPlacesLow: '{places} (w tym {low} na 0,80 m)',
    rackLevels: 'Wysokości poziomów (w świetle, od dołu)',
    rackCompartment: 'Poziom {n} · {load}',
    rackTop: 'Poziom {n} górny · {load}',
    rackTopClear: '{clear} do górnej krawędzi ramy',
    rackLoadHigh: 'paleta 1,60 m',
    rackLoadLow: 'paleta 0,80 m',
    rackLoadNone: 'pusty',
    rackResetLevels: 'Standardowe wysokości poziomów (1,75 m)',
    rackPassages: 'Przejścia',
    rackPassageInfo:
      'Pole jako przejście: do wysokości przejścia nie ma w nim belek ani palet, wyżej regał biegnie dalej. Pod pierwszą belką znajduje się siatka ochronna. Podczas zwiedzania można przez nie przejść.',
    rackPassageBay: 'Pole {n} jako przejście',
    rackPassageHeight: 'Wysokość przejścia',
    rackPassageFree: 'Wolne (szerokość × wysokość)',
    rackPassageNarrow:
      'Pole ma tylko {width} szerokości – dla przejścia 2,00 m wybierz regał z dłuższymi belkami.',
    shelfPlaces: 'Miejsca (karton 30 × 60 cm)',
    shelfArea: 'Powierzchnia półek',
    shelfLayout: {
      one: '{n} pole × {levels} półek · {places} miejsc',
      few: '{n} pola × {levels} półek · {places} miejsc',
      many: '{n} pól × {levels} półek · {places} miejsc',
      other: '{n} pola × {levels} półek · {places} miejsc',
    },
    shelfCartons: '3 kartony',
    shelfResetLevels: 'Rozłóż półki równomiernie',
    shelfHint:
      'Długość przeskakuje o całe pola (1,05 m, każde kolejne pole 1,00 m), wysokość co 50 cm. Poziom liczy się, gdy mieści się karton 30 × 60 × 28 cm; pozostała wysokość jest rozkładana równomiernie na kolejne poziomy.',
    rackHint:
      'Długość przeskakuje o całe pola; sąsiednie pola dzielą jedną ramę. Od 1,60 m wysokości w świetle składowana jest paleta 1,60 m, od 0,80 m paleta 0,80 m, w przeciwnym razie poziom pozostaje pusty.',
    clearance: 'Przestrzeń wolna',
    clearanceInfo:
      'Przestrzeń obsługowa i serwisowa wokół obiektu; uwzględniana przy sprawdzaniu kolizji. Wskazówka projektowa, nie ocena bezpieczeństwa.',
    clearanceToggle: 'Przestrzeń obsługowa/serwisowa',
    front: 'Przód',
    back: 'Tył',
    left: 'Lewo',
    right: 'Prawo',
    nativeSize: 'Rozmiar oryginalny',
    scale: 'Skala',
    loadError: 'Nie udało się wczytać modelu – wyświetlany jest zastępnik.',
    restoreSize: 'Przywróć rozmiar oryginalny',
  },
  multi: {
    title: 'Grupa tymczasowa',
    summary:
      'Zaznaczono: {items}. Przeciąganie przesuwa wszystko razem; narzędzie przesuwania pokazuje wspólny uchwyt.',
    deselect: 'Wyczyść zaznaczenie',
    deselectHint: 'Także klawiszem Esc lub prawym kliknięciem w puste miejsce.',
    rotateTogether: 'Obróć razem (wokół środka zaznaczenia)',
    alignCount: 'Wyrównaj ({items})',
    alignAria: 'Wyrównaj',
    align: {
      left: 'Wyrównaj do lewej',
      'center-x': 'Wyśrodkuj w poziomie',
      right: 'Wyrównaj do prawej',
      top: 'Wyrównaj do góry',
      'center-y': 'Wyśrodkuj w pionie',
      bottom: 'Wyrównaj do dołu',
    },
    distribute: 'Rozmieść',
    distributeX: 'Równomiernie w poziomie',
    distributeY: 'Równomiernie w pionie',
    gapAria: 'Stały odstęp (m)',
    gapX: '↔ Odstęp',
    gapY: '↕ Odstęp',
    gapHint:
      '„Odstęp” ustawia obiekty w rzędzie z dokładnie tym wolnym odstępem, np. dla alejek regałowych.',
    alongWall: 'Przesuń wzdłuż ściany ({items})',
    alongAria: 'Przesunięcie wzdłuż ściany (m)',
    towardsStart: '← W stronę początku ściany',
    towardsEnd: 'W stronę końca ściany →',
    spreadOnWall: 'Rozmieść równomiernie na ścianie',
    slideBlocked: 'Na drodze stoi już inny otwór.',
    sill: 'Wysokość parapetu ({items})',
    liftAria: 'Zmiana wysokości (m)',
    raise: '↑ Wyżej',
    lower: '↓ Niżej',
    storeyAbove: 'Przenieś na kondygnację wyżej',
    storeysAll: 'Na wszystkie wyższe kondygnacje',
    storeyHint: 'Drzwi z parteru zostają na dole.',
    copied: 'Skopiowano ✓',
    pasteHint:
      'Skopiuj, kliknij ścianę docelową (np. na piętrze lub z tyłu) i naciśnij Ctrl+V – układ pozostaje taki sam.',
    placement: {
      'no-wall': 'Najpierw kliknij ścianę docelową, potem wklej.',
      'no-room': 'Na tej ścianie nie ma na nie miejsca.',
      'too-tall': 'Otwory są wyższe niż ta ściana.',
      overlap: 'W tym miejscu są już inne otwory.',
    },
  },
  outdoorPanels: {
    points: 'Punkty ({n})',
    insertPoint: 'Wstaw punkt za tym',
    fireLane: 'Drogi pożarowe wymagają co najmniej 3 m szerokości w świetle (DIN 14090).',
    extendRoad: 'Aby przedłużyć, zacznij na końcu drogi w trybie rysowania „Droga”.',
    carPark: 'Parking dla samochodów osobowych',
    truckStalls: 'Miejsca postojowe dla ciężarówek',
    perRow: 'Miejsca w rzędzie',
    rows: 'Rzędy',
    rows1: '1 (droga manewrowa z przodu)',
    rows2: '2 (droga manewrowa pośrodku)',
    stallWidth: 'Szerokość miejsca',
    stallDepth: 'Głębokość miejsca',
    aisle: 'Droga manewrowa',
    roofU: 'Położenie w poprzek (u)',
    roofV: 'Położenie wzdłuż (v)',
    roofHint:
      'Położenie względem środka hali. Przeciągaj na rzucie, zmieniaj rozmiar uchwytami po bokach („Zmień rozmiar”). Na dachu płaskim dowolny obrót, na dachach spadzistych co 90°.',
    toHall: 'Do hali ({name})',
  },
  zoneRoom: {
    alignToBuilding: 'Wyrównaj do budynku',
    editVertices: 'Edytuj narożniki',
    floorColor: 'Kolor posadzki',
    roomArea: 'Powierzchnia pomieszczenia',
    walls: 'Ściany',
    sharedWalls: {
      one: 'Jedna strona korzysta ze ściany sąsiedniego pomieszczenia lub budynku.',
      few: '{n} strony korzystają ze ściany sąsiedniego pomieszczenia lub budynku.',
      many: '{n} stron korzysta ze ściany sąsiedniego pomieszczenia lub budynku.',
      other: '{n} strony korzystają ze ściany sąsiedniego pomieszczenia lub budynku.',
    },
    recalcWalls: 'Przelicz ściany',
  },
  measurementPanel: {
    title: 'Zapisany wymiar',
  },
  polygon: {
    edge: 'Krawędź P{a}–P{b}',
    insertTitle: 'Wstaw punkt na tej krawędzi',
    add: '+ Punkt',
    remove: '− Punkt',
    deleteTitle: 'Usuń punkt',
  },
  background: {
    title: 'Obraz tła',
    empty: 'Plan sytuacyjny lub zdjęcie lotnicze',
    tools: { scale: 'Skala', align: 'Punkt do punktu', move: 'Przesuń' },
    toolHints: {
      scale: 'Kliknij dwa punkty znanego odcinka na obrazie',
      align: 'Kliknij punkt obrazu, a potem punkt docelowy na planie',
      move: 'Przeciągnij obraz na planie',
    },
    uploading: 'Przesyłanie …',
    upload: 'Prześlij obraz lub PDF (JPG/PNG/PDF)',
    hint: 'Np. zrzut ekranu z geoportalu, zeskanowany plan sytuacyjny lub rysunek PDF (ostry przy powiększaniu, biel staje się przezroczysta). Następnie kliknij dwa punkty znanego odcinka, aby ustalić skalę.',
    levelAria: 'Kondygnacja, której plan jest edytowany',
    hintLevel:
      'Plan dla: {level}, np. rysunek PDF piętra. Widoczny, gdy plan pokazuje tę kondygnację; potem „Przejmij skalę i położenie z parteru” lub skalibruj na nowo.',
    onTop: 'Pokaż nad planowaniem',
    onTopHint:
      'Plan leży nad halami, biurami i pomieszczeniami, aby go odrysować; kliknięcia, rysowanie i przyciąganie przechodzą przez niego. PDF ma przezroczysty papier.',
    fromGround: 'Przejmij skalę i położenie z parteru',
    fromGroundHint:
      'Kładzie ten plan dokładnie na planie parteru (ten sam środek, obrót i skala) – dla planów tego samego budynku na arkuszach tej samej wielkości.',
    pdfPage: '„{name}” ma {n} stron – która ma być tłem?',
    pdfPageOption: 'Strona {n}',
    pdfInsert: 'Wstaw stronę',
    show: 'Pokaż na planie',
    opacity: 'Krycie',
    opacityValue: 'Krycie {pct} %',
    size: 'Wymiary',
    align: 'Wyrównaj',
    alignInfo:
      'Skala: kliknij dwa punkty znanego odcinka na obrazie. Punkt do punktu: najpierw punkt obrazu, potem punkt docelowy na planie. Przesuń: przeciągnij obraz na planie. Na koniec zablokuj.',
    alignAria: 'Wyrównaj tło',
    other: 'Inny obraz …',
    remove: 'Usuń',
    takeOver: 'Przejmij działkę i tło z pliku projektu …',
    takeOverHint:
      'Z wyeksportowanego pliku .indusketch (np. szablonu z dobrze narysowanym planem sytuacyjnym). Zastępuje działki i tło parteru; budynki i reszta pozostają.',
    takeOverFrom: 'Przejąć z „{name}”?',
    takeOverSites: {
      one: '• {n} działka – zastępuje dotychczasowe działki',
      few: '• {n} działki – zastępują dotychczasowe działki',
      many: '• {n} działek – zastępuje dotychczasowe działki',
      other: '• {n} działki – zastępują dotychczasowe działki',
    },
    takeOverImage: '• obraz tła – zastępuje dotychczasowy na parterze',
    takeOverKeeps:
      'Budynki, wyposażenie wnętrz, obiekty i zagospodarowanie terenu pozostają bez zmian. Następnie przesuń działki i obraz razem pod budynek; cofnij za pomocą Ctrl+Z.',
    takeOverEmpty: 'Plik nie zawiera ani działek, ani obrazu tła.',
    takeOverMissing: 'W pliku brakuje obrazu tła – wyeksportuj szablon jako pakiet .indusketch.',
  },
  videos: {
    title: 'Wideo',
    none: 'Brak',
    count: { one: '{n} wideo', other: '{n} wideo' },
    empty: 'Brak nagrań. Podczas spaceru w 3D nagraj obchód przyciskiem „Nagrywaj”.',
    play: 'Odtwórz',
    download: 'Pobierz',
    delete: 'Usuń',
    confirmDelete: 'Trwale usunąć wideo „{name}”?',
    nameAria: 'Nazwa wideo',
    meta: '{date} · {duration} · {size}',
    by: 'nagrał(a) {name}',
    withSound: 'z dźwiękiem',
    quota: 'zajęte {used} z {quota}',
    upload: 'Prześlij plik wideo …',
    uploadHint: 'MP4 lub WebM, maksymalnie {max}',
    loadFailed: 'Nie udało się wczytać wideo: {reason}',
  },
  project: {
    title: 'Ustawienia projektu',
    project: 'Projekt',
    description: 'Opis',
    grid: 'Siatka i przyciąganie',
    snapOn: 'przyciąganie wł.',
    gridSize: 'Rozstaw siatki',
    customGrid: 'Własny rozstaw siatki',
    showGrid: 'Pokaż siatkę',
    snap: 'Przyciągaj do siatki',
    collisions: 'Sprawdzaj kolizje',
    alignToBuilding: 'Wyrównaj do budynku',
    alignToBuildingHint:
      'W budynkach ustawionych ukośnie siatka i przyciąganie biegną wzdłuż ścian; nowe pomieszczenia, strefy, ściany i obiekty stoją równolegle do budynku.',
    display: 'Widok',
    displaySummary: '{unit} · dach {roof}',
    unit: 'Jednostka',
    roof3d: 'Dach w 3D',
    roofAria: 'Wyświetlanie dachu',
    roofModes: { visible: 'Widoczny', transparent: 'Przezroczysty', hidden: 'Wył.' },
    wallsTransparent: 'Przezroczyste ściany',
    structureVisible: 'Pokaż konstrukcję stalową hal',
    structureRoof: 'Pokaż konstrukcję dachu (rygle, płatwie, stężenia)',
    loads: 'Obciążenia (wstępne wymiarowanie)',
    loadsInfo:
      'Strefy obciążenia śniegiem i wiatrem wg EN 1991-1-3 i EN 1991-1-4 z załącznikiem krajowym danego kraju (Niemcy: mapy stref np. w DIBt; Polska: PN-EN ze strefami śniegowymi 1–5 i wiatrowymi 1–3) oraz wysokość terenu działki. Na tej podstawie wstępnie wymiarowane są ramy, dźwigary, płatwie, rygle ścienne, słupy i podciągi. Bez podania obowiązuje 0,85 kN/m² śniegu na dachu i 0,65 kN/m² ciśnienia wiatru.',
    loadsSummary: '{country} · strefa {zone} · {altitude} m n.p.m.',
    country: 'Kraj (normy obciążeń)',
    countries: { de: 'Niemcy', pl: 'Polska' },
    windZone: 'Strefa wiatrowa',
    windZoneDefault: { de: 'Typowa (strefa 2)', pl: 'Typowa (strefa 1)' },
    terrain: 'Teren',
    terrainDefault: { de: 'Typowy (śródlądzie)', pl: 'Typowy (kategoria III)' },
    terrains: {
      inland: 'Śródlądzie',
      coast: 'Wybrzeże i wyspy',
      open: 'Kategoria II – teren otwarty',
      suburban: 'Kategoria III – przedmieścia, przemysł',
      urban: 'Kategoria IV – miasto',
    },
    windPressure: 'Ciśnienie wiatru qₚ do 10 m',
    snowZone: 'Strefa obciążenia śniegiem',
    snowZoneNone: 'Brak (typowe obciążenie śniegiem)',
    snowZoneLabel: 'Strefa {zone}',
    altitude: 'Wysokość terenu (m n.p.m.)',
    groundSnow: 'Śnieg na gruncie (sₖ)',
    roofLoad: 'Obciążenie dachu (śnieg + ciężar własny)',
    loadValue: '{value} kN/m²',
    shortcuts: 'Skróty klawiszowe',
  },
  props: {
    aria: 'Właściwości',
    back: 'Ustawienia projektu',
    backTitle: 'Powrót do ustawień projektu',
    resizeStop: 'Zakończ zmianę rozmiaru (E)',
    shell: 'Chroń bryłę',
    shellHint:
      'Położenie, rozmiar i obrys są dodatkowo chronione – także przed „Odblokuj wszystko”. Wnętrze, okna, drzwi i bramy można dalej edytować.',
    shellConfirm: 'Naprawdę zwolnić bryłę „{name}”? Potem można ją znów przesuwać i zmieniać.',
    resizeStart: 'Zmień rozmiar (E)',
    moveStart: 'Przesuń (S)',
    resizeTitle: 'Przeciągaj uchwyty na końcach lub bokach – klawisz E włącza i wyłącza je',
    moveTitle: 'Przeciągnij uchwyt przesuwania elementu – klawisz S',
  },
  rotation: {
    by: 'Obróć o',
    angle: 'Obrót',
    ccw90: '90° przeciwnie do ruchu wskazówek zegara',
    straighten: 'Wyprostuj (pod kątem prostym do hali)',
    cwFine: '0,5° zgodnie z ruchem wskazówek zegara (strzałka w dół)',
    ccwFine: '0,5° przeciwnie do ruchu wskazówek zegara (strzałka w górę)',
    cw90: '90° zgodnie z ruchem wskazówek zegara',
    steps: '{label}: kroki 90° i 0,5°',
  },
  lock: {
    label: '🔒 Zablokuj',
    hint: 'Zablokowano przed przesuwaniem, obracaniem i przeciąganiem. Wartości tutaj można nadal zmieniać.',
  },
  info: {
    about: 'Wskazówka: {title}',
    plain: 'Wskazówka',
    show: 'Pokaż wskazówkę',
  },
  outdoor: {
    drawTitle: 'Rysuj na planie',
    drawInfo:
      'Drogi, parkingi i ogrodzenia można przesuwać na planie; szerokość, rodzaj i punkty dopasuj po prawej. Rozpoczęcie na końcu drogi lub ogrodzenia je przedłuża.',
    drawRoad: 'Rysuj drogę / ścieżkę',
    drawParking: 'Rysuj parking',
    drawFence: 'Rysuj ogrodzenie',
    surfacesTitle: 'Nawierzchnie i tereny zielone',
    surfacesInfo:
      'Wybierz rodzaj, a następnie klikaj narożniki na planie (np. na planie sytuacyjnym) albo szybko przeciągnij prostokąt. Powierzchnie pojawią się w zestawieniu powierzchni i w PDF w sekcji „Powierzchnie zewnętrzne”.',
    parkingTitle: 'Parking według liczby miejsc',
    stalls: 'Miejsca postojowe',
    car: 'Osobowe',
    truck: 'Ciężarowe',
    countAria: 'Liczba miejsc postojowych',
    invalidCount: 'Podaj od 1 do 400 miejsc postojowych.',
    insertParking: 'Wstaw parking',
    fenceTitle: 'Ogrodzenie',
    fenceInfo:
      'Biegnie wzdłuż zewnętrznej granicy wszystkich działek; granice między własnymi działkami pozostają wolne.',
    fenceAlongSite: 'Ogrodzenie wzdłuż granicy działki',
  },
  dock: {
    title: 'Rampa załadunkowa',
    info: 'Brama z obniżonym dojazdem: 16 m płaskiego stanowiska, rampa 10 %, głębokość 1,20 m, pomost przeładunkowy 2 × 3 m. Kilka ramp stoi w rozstawie osi 4 m we wspólnym dojeździe, z balustradą tylko po skrajnej lewej i prawej stronie.',
    single: 'Rampa pojedyncza',
    double: 'Rampa podwójna',
    count: 'Liczba ramp',
    row: 'Rampy obok siebie: {n}',
    place: 'Umieść na ścianie hali',
    needsHall: 'Najpierw utwórz halę – rampa znajduje się w ścianie hali.',
    placeHint: 'Teraz dotknij ściany hali, przy której ma być rampa.',
  },
  openings: {
    placeHint: 'Dotknij ściany na planie lub w 3D – dowolnie wiele po kolei.',
  },
  library: {
    search: 'Szukaj obiektu …',
    searchAria: 'Szukaj obiektu',
    placeHint: 'Dotknij planu lub widoku 3D, aby umieścić. R obraca, Shift+klik umieszcza kolejne.',
    nothingFound: 'Nic nie znaleziono.',
    sizeRefs: 'Odniesienia wielkości',
    sizeRef: 'Odniesienie wielkości {name}',
    ownModels: 'Własne modele 3D',
    uploading: 'Przesyłanie i sprawdzanie …',
    addGlb: 'Dodaj model GLB',
    dropHint: 'Przeciągnij plik tutaj lub wybierz · również Draco/Meshopt',
    system: 'Biblioteka systemowa',
    heightShort: 'W',
  },
  placed: {
    none: 'Nie rozmieszczono jeszcze obiektów.',
    hidden: 'ukryte: {n}',
    showAll: 'Pokaż wszystkie',
    show: 'Pokaż: {label}',
    hide: 'Ukryj: {label}',
    countHidden: '{n} (ukryte: {hidden})',
    focus: 'Zaznacz i przybliż',
  },
  measure: {
    save: 'Zapisz jako wymiar',
    hint: 'Przeciągaj na planie od punktu do punktu. Narożniki, krawędzie i ściany są przyciągane.',
  },
  selectOptions: {
    multi: 'Wybór wielokrotny (dotknięcie dodaje)',
    multiTitle: 'Dotknięcie dodaje do zaznaczenia lub z niego usuwa',
    distances: 'Odległości od wybranego obiektu',
    distancesTitle: 'Wolne odległości wybranego obiektu od otoczenia',
  },
  keys: {
    paste: 'Ctrl+V',
    copy: 'Ctrl+C',
    cut: 'Ctrl+X',
    duplicate: 'Ctrl+D',
    selectAll: 'Ctrl+A',
    rotate: 'R',
    rotateBack: 'Shift+R',
    zoomSelection: 'F',
    move: 'S',
    resize: 'E',
    delete: 'Del',
  },
  shortcuts: {
    undo: { keys: 'Ctrl+Z / Ctrl+Y', text: 'Cofnij / Ponów' },
    save: { keys: 'Ctrl+S', text: 'Zapisz' },
    clipboard: { keys: 'Ctrl+C / X / V', text: 'Kopiuj / Wytnij / Wklej' },
    duplicate: { keys: 'Ctrl+D', text: 'Duplikuj' },
    selectAll: { keys: 'Ctrl+A', text: 'Zaznacz wszystkie obiekty' },
    delete: { keys: 'Del', text: 'Usuń zaznaczenie' },
    nudge: { keys: 'Strzałki', text: 'Przesuń zaznaczenie o jeden krok siatki (Shift: ×10)' },
    rotate: { keys: 'R / Shift+R', text: 'Obróć obiekt o 90° (także obiekt przy kursorze)' },
    placeMore: {
      keys: 'Shift+klik przy umieszczaniu',
      text: 'Umieść obiekt i od razu następny',
    },
    tools: {
      keys: 'V · W · S · R · M',
      text: 'Narzędzie Wybór · Rysuj · Przesuń · Obróć · Mierz',
    },
    resize: { keys: 'E', text: 'Zmień rozmiar: uchwyty na wybranym elemencie wł./wył.' },
    multi: {
      keys: 'Shift+klik / Shift+przeciąganie',
      text: 'Wybór wielokrotny / zaznaczanie ramką – prawe kliknięcie w puste miejsce lub Esc je kasuje',
    },
    zoomSelection: { keys: 'F', text: 'Przybliż do zaznaczenia (plan 2D)' },
    grid: { keys: 'G', text: 'Pokaż/ukryj siatkę' },
    zoom: { keys: '+ / −', text: 'Przybliż / oddal plan 2D' },
    pan: {
      keys: 'Spacja + przeciąganie',
      text: 'Przesuń plan lub widok 3D – w każdym narzędziu, także w trakcie rysowania',
    },
    siteDraft: {
      keys: 'Enter / Backspace',
      text: 'Rysowanie punkt po punkcie (działka, powierzchnia, droga, ogrodzenie): zakończ / usuń ostatni punkt',
    },
    escape: {
      keys: 'Esc',
      text: 'Odrzuć rysowaną działkę, zakończ zmianę rozmiaru, potem anuluj zaznaczenie, pomiar i umieszczanie',
    },
  },
  drive: {
    getIn: 'Wsiądź (E)',
    getOut: 'Wysiądź (E)',
    forkUp: 'Widły ▲ (R)',
    forkDown: 'Widły ▼ (F)',
    drop: 'Odstaw (G)',
    view: 'Widok (V)',
    hint: 'W/S: gaz, hamulec, wstecz · A/D: skręt',
    hintForks:
      'W/S: jazda · A/D: skręt · widły pod paletę, potem R, aby podnieść · opuść całkowicie lub G, aby odstawić',
  },
  vr: {
    button: 'VR',
    buttonTitle: 'Przejdź się w goglach VR',
    suggestTitle: 'Wykryto gogle VR',
    suggestText: 'Zwiedzaj budynki i działki w VR.',
    suggestGame: 'Dołącz do gry BHP w goglach VR.',
    projectsHint: 'Otwórz projekt, aby zwiedzać budynki i działki w VR.',
    enter: 'Uruchom VR',
    later: 'Później',
    menuTitle: 'Menu VR',
    help: 'Lewa gałka: chodzenie (do końca: bieg) · Prawa gałka: obrót · Y lub B: menu',
    toStart: 'Do punktu startowego',
    turnSnap: 'Obrót: skokowy (30°)',
    turnSmooth: 'Obrót: płynny',
    startGame: 'Rozpocznij grę BHP',
    endGame: 'Zakończ grę BHP',
    exit: 'Zakończ VR',
    close: 'Zamknij menu',
  },
  context: {
    pasteHere: 'Wklej tutaj',
    selectAll: 'Zaznacz wszystkie obiekty',
    selectEverything: 'Zaznacz wszystko',
    lockEverything: 'Zablokuj wszystko',
    unlockEverything: 'Odblokuj wszystko',
    protectShells: 'Chroń wszystkie bryły budynków i działki',
    deselect: 'Wyczyść zaznaczenie',
    rotateLeft90: 'Obróć o 90° w lewo',
    straighten: 'Wyprostuj',
    rotateRight90: 'Obróć o 90° w prawo',
    rotateLeftFine: 'Obróć o 0,5° w lewo',
    rotateRightFine: 'Obróć o 0,5° w prawo',
    selectAllSites: 'Zaznacz wszystkie działki',
    cut: 'Wytnij',
    duplicateOpening: 'Duplikuj (następne wolne miejsce)',
    unlock: 'Odblokuj',
    lock: 'Zablokuj (przed przesuwaniem)',
    resize: 'Zmień rozmiar',
    move: 'Przesuń',
    insertVertex: 'Wstaw tutaj narożnik',
    railingEdgeOff: 'Usuń balustradę na tej krawędzi',
    railingEdgeOn: 'Przywróć balustradę na tej krawędzi',
    removeVertex: 'Usuń najbliższy narożnik (P{n})',
    zoomSelection: 'Przybliż do zaznaczenia',
    attachSite: 'Dołącz działkę …',
  },
  plan: {
    aria: 'Rzut 2D',
    zoom: 'Powiększenie',
    zoomIn: 'Przybliż (+)',
    zoomOut: 'Oddal (−)',
    zoomSelection: 'Przybliż do zaznaczenia (F)',
    grid: 'Pokaż/ukryj siatkę (G)',
    dimsToggle: 'Pokaż/ukryj wymiary i opisy',
    dimsMenu: 'Wymiary i opisy',
    dims: 'Wymiary',
    labels: 'Opisy',
    dimItems: {
      site: 'Krawędzie działki',
      building: 'Wymiary hali',
      measurements: 'Zapisane wymiary',
      grid: 'Siatka osi i pola',
      distances: 'Odległości (obiekt)',
    },
    labelItems: {
      site: 'Działki (nazwa, m²)',
      building: 'Hale i budynki biurowe',
      mezzanines: 'Antresole (nazwa, m²)',
      interior: 'Strefy i pomieszczenia',
      areas: 'Obszary (nazwa, m²)',
      outdoor: 'Drogi i miejsca postojowe',
      objects: 'Nazwy obiektów',
      symbols: 'Rampy załadunkowe i schody',
    },
    tapWall: 'Dotknij bezpośrednio ściany, aby wstawić otwór.',
    stallsPreview: {
      one: '{n} miejsce postojowe ({rows} × {perRow})',
      few: '{n} miejsca postojowe ({rows} × {perRow})',
      many: '{n} miejsc postojowych ({rows} × {perRow})',
      other: '{n} miejsca postojowego ({rows} × {perRow})',
    },
    tooSmall: 'Za mało miejsca na miejsce postojowe',
    north: 'Północ jest u góry',
    closeHere: 'Kliknij tutaj, aby zamknąć',
    snap: {
      corner: 'Narożnik',
      midpoint: 'Środek',
      edge: 'Krawędź',
      wall: 'Ściana',
      grid: 'Siatka',
    },
    siteTooFew: 'Działka wymaga co najmniej trzech narożników.',
    siteSelfIntersect: 'Granica działki nie może się przecinać.',
    areaTooFew: 'Powierzchnia wymaga co najmniej trzech narożników.',
    areaSelfIntersect: 'Obrys nie może się przecinać.',
    mezzanineNoHall: 'Antresolę trzeba narysować wewnątrz hali.',
    lineTooShort: 'Linia jest za krótka.',
  },
  planBackground: {
    scale1: 'Kliknij pierwszy punkt znanego odcinka na obrazie',
    scale2: 'Kliknij drugi punkt odcinka',
    align1: 'Kliknij punkt na obrazie (np. narożnik działki)',
    align2: 'Kliknij punkt docelowy na planie – przyciąga się do narożników',
    move1: 'Przesuwaj obraz z wciśniętym przyciskiem myszy lub palcem',
    calibrate: 'Odcinek na obrazie obecnie {length}. Rzeczywista długość:',
    realLength: 'Rzeczywista długość',
    example: 'np. 48,5',
    apply: 'Zastosuj',
  },
  resizeLabels: {
    edges: 'Krawędzie {a} / {b}',
    edge: 'Krawędź {length} · przesunięcie {shift}',
    length: 'Długość {length}',
    width: 'Szerokość {length}',
    stalls: {
      one: '{n} miejsce postojowe · {length}',
      few: '{n} miejsca postojowe · {length}',
      many: '{n} miejsc postojowych · {length}',
      other: '{n} miejsca postojowego · {length}',
    },
  },
  dashboard: {
    aria: 'Zestawienie powierzchni',
    title: 'Powierzchnie',
    headlineBuilt: 'Zabudowa {built} · wolne w hali {free}',
    headlineSite: 'Działka {area}',
    stillFree: 'jeszcze wolne',
    mezzanine: 'Antresola {name}',
    storeyArea: 'Powierzchnia kondygnacji razem',
    totalStoreyArea: 'Powierzchnie kondygnacji razem',
    officeMode: 'Tryb biura',
    daylight: 'Światło dzienne dach',
    traffic: 'Powierzchnie komunikacyjne',
    roads: 'Drogi / ścieżki',
    parking: 'Parkingi',
    carStalls: 'Miejsca dla samochodów osobowych',
    fenceLength: 'Długość ogrodzenia',
    area: 'Obszar {n}',
    palletPlaces: 'Miejsca paletowe',
    palletPlacesLow: 'w tym na palety 0,80 m',
    palletRacks: {
      one: 'w {n} regale paletowym',
      few: 'w {n} regałach paletowych',
      many: 'w {n} regałach paletowych',
      other: 'w {n} regału paletowego',
    },
    shelfPlaces: 'Miejsca na półkach',
    shelfArea: 'Powierzchnia półek',
    shelfRacks: {
      one: 'w {n} regale półkowym',
      few: 'w {n} regałach półkowych',
      many: 'w {n} regałach półkowych',
      other: 'w {n} regału półkowego',
    },
    outdoorZones: 'Powierzchnie zewnętrzne',
    built: 'Powierzchnia zabudowy',
    freeSite: 'Wolna powierzchnia działki',
    coverage: 'Stopień zabudowy',
    more: '… i jeszcze {n} wskazówek',
    warnings: {
      one: '{n} wskazówka',
      few: '{n} wskazówki',
      many: '{n} wskazówek',
      other: '{n} wskazówki',
    },
    warningsToggle: 'Pokaż lub ukryj wskazówki',
  },
  status: {
    mode: 'Tryb: {tool}',
    grid: 'Siatka: {size}',
    snap: 'Przyciąganie: {state}',
    on: 'wł.',
    off: 'wył.',
  },
  officeMode: {
    aria: 'Tryb biurowy',
    title: 'Tryb biurowy · {name}',
    exit: 'Zakończ tryb biurowy',
    slabsAria: 'Pokaż/ukryj stropy',
    showSlab: 'Pokaż strop',
    hideSlab: 'Ukryj strop',
    slabAbove: 'Strop nad {level}',
  },
  scene: {
    aria: 'Widok 3D',
    sectionTitle: 'Przekrój przez kondygnację: wszystko powyżej jest ukryte',
    sectionAria: 'Przekrój kondygnacji',
    allLevels: 'Wszystkie kondygnacje',
    sectionAt: 'Przekrój {level}',
    slabs: 'Stropy',
    grid: 'Siatka',
    shadows: 'Cienie',
    shadowsTitle: 'Miękkie cienie (obciążają tablety)',
    showRoof: 'Pokaż dach',
    hideRoof: 'Ukryj dach',
    structure: 'Szkielet stalowy',
    structureTitle: 'Pokaż lub ukryj słupy i rygle hal',
    structureRoof: 'Konstrukcja dachu',
    structureRoofTitle:
      'Pokaż lub ukryj rygle, płatwie, stężenia dachowe i skosy; słupy i rygle ścienne pozostają',
    screenshot: 'Zrzut ekranu',
    options: 'Widok',
    walk: 'Spacer',
    walkTitle: 'Przejdź przez obiekt jako osoba',
    walkStop: 'Zakończ spacer',
    walkPlace: 'Kliknij w scenę, gdzie chcesz zacząć.',
    walkHint:
      '↑ ↓: chodzenie · ← →: obrót · Shift: tryb biegu wł./wył. · V: widok · przytrzymaj B: spojrzenie do tyłu · przeciąganie myszą: rozglądanie · klik: wybór · przeciągnij obiekt: przesuń · Esc: koniec',
    walkHintTouch:
      'Joystick: przód/tył chodzenie, lewo/prawo obrót · przesuwanie: rozglądanie · dotknięcie: wybór · przeciągnij obiekt: przesuń',
    walkJoystick: 'Chodzenie',
    walkRun: 'Bieg',
    walkRunTitle: 'Tryb biegu wł./wył. (klawisz: Shift); bieg tylko z nim',
    walkViews: { eye: 'Własne oczy', shoulder: 'Przez ramię', behind: 'Z tyłu' },
    walkViewTitle: 'Zmień widok: własne oczy, przez ramię, z tyłu (klawisz: V)',
    walkBack: 'Do tyłu',
    walkBackTitle: 'Przytrzymaj: spojrzenie do tyłu (klawisz: B)',
    record: 'Nagrywaj',
    recordTitle: 'Nagraj spacer jako wideo (pobierane po zakończeniu)',
    recordStop: 'Zakończ nagrywanie',
    recording: 'Nagrywanie',
    recordSaved: 'Wideo zapisane: {file}',
    walkVideoLabel: 'Spacer',
    microphone: 'Mikrofon',
    microphoneTitle: 'Nagrywaj własny komentarz przez mikrofon do wideo (od następnego nagrania)',
    micUnavailable: 'Brak mikrofonu lub brak zgody – wideo jest nagrywane bez dźwięku.',
    videoQualityHigh: 'Wysoka',
    videoQualityStandard: 'Standard',
    videoQualityTitle:
      'Jakość wideo: standard (720p, ok. 22 MB na minutę) lub wysoka (Full HD, ok. 60 MB na minutę); od następnego nagrania',
    recordFailed:
      'Nagranie nie powiodło się. Spróbuj ponownie, w razie potrzeby w jakości standard.',
    videoUploaded: 'Wideo zapisane w projekcie: {file}',
    videoUploadFailed:
      'Nie udało się zapisać wideo w projekcie ({reason}). Plik został pobrany i można go później przesłać w sekcji „Wideo”.',
    videoUploading: 'Zapisywanie wideo w projekcie … {percent} %',
  },
  spaceMouse: {
    connectedAria: 'SpaceMouse połączona: {device}',
    connect: 'Połącz SpaceMouse',
    connected: 'Połączono: {device}',
    notConnected: 'Nie połączono',
    ratesTitle:
      'Mało sygnałów: urządzenie/sterownik. Mało klatek: komputer zbyt wolno rysuje scenę 3D.',
    rates: 'Diagnostyka: {reports} sygnałów/s · {frames} klatek/s',
    speed: 'Prędkość {pct} %',
    invertPan: 'Odwróć przesuwanie',
    invertZoom: 'Odwróć powiększanie',
    invertRotate: 'Odwróć obracanie',
    help: '2D: przesunięcie nakładki przesuwa, naciśnięcie/pociągnięcie powiększa. 3D: przesunięcie przesuwa, do przodu/do tyłu powiększa, przechylenie i obrót obracają widok. Przyciski: Menu przełącza 2D/3D, Fit pokazuje wszystko, T/F/R wybierają Góra/Przód/Bok. Tylko w Chrome i Edge.',
    busy: 'SpaceMouse jest używana przez inny program lub kartę.',
    failed: 'Połączenie niemożliwe.',
  },
  screenshot: {
    title: 'Zrzut ekranu widoku 3D',
    hint: 'Rejestrowany jest bieżący widok – bez pasków narzędzi, zaznaczenia i uchwytów.',
    resolution: 'Rozdzielczość',
    a4: 'A4 poziomo, 300 dpi (3508 × 2480)',
    background: 'Tło',
    backgrounds: { white: 'Białe', transparent: 'Przezroczyste', scene: 'Jak w widoku' },
    creating: 'Tworzenie …',
    save: 'Zapisz obraz',
  },
  report: {
    title: 'Raport projektowy PDF',
    intro:
      'Raport zawiera dane projektu, wskaźniki, rzut w skali z legendą, dwie perspektywy 3D oraz zestawienia powierzchni, bram i obiektów. Tworzony jest z zapisanego stanu; niezapisane zmiany są najpierw zapisywane.',
    includeCosts: 'Dołącz ilości i szacunek kosztów (ceny jednostkowe z ustawień projektu)',
    omitHidden: {
      one: 'Pomiń w raporcie {n} ukryty obiekt',
      few: 'Pomiń w raporcie {n} ukryte obiekty',
      many: 'Pomiń w raporcie {n} ukrytych obiektów',
      other: 'Pomiń w raporcie {n} ukrytego obiektu',
    },
    steps: {
      saving: 'Zapisywanie bieżącego stanu …',
      rendering: 'Tworzenie widoków 3D',
      generating: 'Tworzenie pliku PDF …',
    },
    stepOf: '{i} z {n}',
    language: 'Język raportu',
    done: 'Raport został pobrany.',
    create: 'Utwórz PDF',
  },
  messages: {
    commandFailed: '„{label}” nie powiodło się. Projekt nie został zmieniony.',
    commandRejected: '„{label}” niemożliwe',
    commandRejectedWhy: '„{label}” niemożliwe: {reason}',
    saveConflict: 'Projekt został zmieniony w innym miejscu. Niczego nie nadpisano.',
    resolveConflictFirst: 'Projekt został zmieniony w innym miejscu – najpierw rozwiąż konflikt.',
    saveFailed: 'Zapisywanie nie powiodło się – sprawdź połączenie.',
    noProject: 'Brak otwartego projektu',
    imageRead: 'Nie udało się odczytać obrazu',
    glbOnly: 'Wybierz plik .glb.',
    tooLarge: 'Plik jest za duży (maks. {mb} MB).',
    openFailed: 'Nie można otworzyć projektu: {reason}',
    loadingProject: 'Wczytywanie projektu …',
    closeMessage: 'Zamknij komunikat',
  },
};
